import { Outlet, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Sheet, SheetTrigger, SheetContent, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { TraderGraphIcon } from "./components/TraderGraphIcon";
import { signOut } from 'firebase/auth';
import { appFirebaseAuth } from '@/context';
import { useUserAccounts } from '@trader-dashboard/shared';
import { useEffect, useState } from 'react';
import { isDemoAccount } from '@/data/demoAccountHelper';
import {
  LayoutDashboard,
  ArrowRightLeft,
  ClipboardList,
  Wallet,
  TrendingUp,
  LineChart,
  FileText,
  Bell,
  User,
  Users,
  LogOut,
  ChevronsLeft,
  ChevronsRight,
  Menu,
} from 'lucide-react';

type NavItem = {
  label: string;
  icon: React.ReactNode;
  to: string;
  matchPath?: string;
};

export const DashboardLayout = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectAccount, loaded: accountsLoaded, accounts, selectedAccountId, defaultAccountId } = useUserAccounts();
  const [collapsed, setCollapsed] = useState(false);

  const lastUsedAccountId = typeof window !== 'undefined' ? window.localStorage.getItem('lastUsedAccountId') || undefined : undefined;
  const fallbackAccountId = selectedAccountId || lastUsedAccountId || defaultAccountId || accounts?.[0]?.id;
  const activeAccountId = accountId || fallbackAccountId;
  const basePath = activeAccountId ? `/account/${activeAccountId}` : '/accounts';

  useEffect(() => {
    if (!accountId || !accountsLoaded) return;
    if (isDemoAccount(accountId)) return;
    const accountExists = accounts?.some(acc => acc.id === accountId);
    if (accountExists) {
      selectAccount(accountId);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('lastUsedAccountId', accountId);
      }
    }
  }, [accountId, accountsLoaded, accounts, selectAccount]);

  const handleLogoutClick = async () => {
    signOut(appFirebaseAuth);
    navigate('/login');
  };

  const mainNavItems: NavItem[] = [
    { label: 'Dashboard', icon: <LayoutDashboard className="h-5 w-5" />, to: basePath, matchPath: basePath },
    { label: 'Pairs', icon: <ArrowRightLeft className="h-5 w-5" />, to: `${basePath}/pairs`, matchPath: `${basePath}/pairs` },
    { label: 'Orders', icon: <ClipboardList className="h-5 w-5" />, to: `${basePath}/orders`, matchPath: `${basePath}/orders` },
    { label: 'Funds', icon: <Wallet className="h-5 w-5" />, to: `${basePath}/funds`, matchPath: `${basePath}/funds` },
    { label: 'Trends', icon: <TrendingUp className="h-5 w-5" />, to: `${basePath}/trends`, matchPath: `${basePath}/trends` },
    { label: 'Analysis', icon: <LineChart className="h-5 w-5" />, to: `${basePath}/analysis`, matchPath: `${basePath}/analysis` },
    { label: 'Events', icon: <Bell className="h-5 w-5" />, to: `${basePath}/events`, matchPath: `${basePath}/events` },
    { label: 'Reports', icon: <FileText className="h-5 w-5" />, to: `${basePath}/reports`, matchPath: `${basePath}/reports` },
  ];

  const bottomNavItems: NavItem[] = [
    { label: 'Profile', icon: <User className="h-5 w-5" />, to: '/profile', matchPath: '/profile' },
    { label: 'Accounts', icon: <Users className="h-5 w-5" />, to: '/accounts', matchPath: '/accounts' },
  ];

  const isActive = (item: NavItem) => {
    if (item.matchPath === basePath && basePath.startsWith('/account/')) {
      return location.pathname === basePath || location.pathname === `${basePath}/`;
    }
    if (item.matchPath === '/profile') {
      return location.pathname === '/profile';
    }
    return location.pathname.startsWith(item.matchPath || item.to);
  };

  const navLinkClasses = (item: NavItem) =>
    `flex items-center gap-3 rounded-lg px-3 py-2 transition-colors ${
      isActive(item)
        ? 'bg-accent text-accent-foreground'
        : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
    }`;

  const renderNavLink = (item: NavItem) => {
    const link = (
      <Link key={item.label} to={item.to} className={navLinkClasses(item)}>
        <span className="shrink-0">{item.icon}</span>
        {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
      </Link>
    );

    if (collapsed) {
      return (
        <Tooltip key={item.label} delayDuration={0}>
          <TooltipTrigger asChild>
            {link}
          </TooltipTrigger>
          <TooltipContent side="right">
            {item.label}
          </TooltipContent>
        </Tooltip>
      );
    }

    return link;
  };

  const renderMobileNavLink = (item: NavItem) => (
    <SheetClose asChild key={item.label}>
      <Link to={item.to} className={navLinkClasses(item)}>
        <span className="shrink-0">{item.icon}</span>
        <span className="text-sm font-medium">{item.label}</span>
      </Link>
    </SheetClose>
  );

  return (
    <div className="flex min-h-screen w-full">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex flex-col border-r bg-background sticky top-0 h-screen overflow-hidden transition-all duration-300 ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        {/* Logo + Collapse Toggle */}
        <div className={`flex items-center h-16 px-3 ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {collapsed ? (
            <button
              onClick={() => setCollapsed(false)}
              className="flex items-center min-w-[24px] min-h-[24px] cursor-pointer"
            >
              <TraderGraphIcon className="h-6 w-6 shrink-0" />
            </button>
          ) : (
            <>
              <Link to={basePath} className="flex items-center gap-2 min-w-[24px] min-h-[24px]">
                <TraderGraphIcon className="h-6 w-6 shrink-0" />
                <span className="text-sm font-semibold whitespace-nowrap">TraderDashboard</span>
              </Link>
              <button
                onClick={() => setCollapsed(true)}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-accent/50"
              >
                <ChevronsLeft className="h-4 w-4" />
              </button>
            </>
          )}
        </div>

        <Separator />

        {/* Main Nav */}
        <nav className="flex flex-col gap-1 p-2 flex-1">
          {mainNavItems.map(renderNavLink)}
        </nav>

        {/* Bottom Section */}
        <div className="mt-auto">
          <Separator />
          <nav className="flex flex-col gap-1 p-2">
            {bottomNavItems.map(renderNavLink)}

            {/* Logout */}
            {collapsed ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleLogoutClick}
                    className="flex items-center gap-3 rounded-lg px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors w-full"
                  >
                    <LogOut className="h-5 w-5 shrink-0" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Logout</TooltipContent>
              </Tooltip>
            ) : (
              <button
                onClick={handleLogoutClick}
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors w-full text-left"
              >
                <LogOut className="h-5 w-5 shrink-0" />
                <span className="text-sm font-medium">Logout</span>
              </button>
            )}
          </nav>

          {/* Expand Toggle (only visible when collapsed) */}
          {collapsed && (
            <>
              <Separator />
              <div className="p-2">
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => setCollapsed(false)}
                      className="flex items-center justify-center rounded-lg px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors w-full"
                    >
                      <ChevronsRight className="h-5 w-5 shrink-0" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Expand</TooltipContent>
                </Tooltip>
              </div>
            </>
          )}
        </div>
      </aside>

      {/* Mobile Header + Content */}
      <div className="flex flex-1 flex-col min-w-0">
        {/* Mobile Top Bar */}
        <header className="sticky top-0 flex h-14 items-center gap-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 md:hidden z-50">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="shrink-0">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle navigation menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              {/* Mobile Logo */}
              <div className="flex items-center gap-2 h-14 px-4">
                <TraderGraphIcon className="h-6 w-6 shrink-0" />
                <span className="text-sm font-semibold">TraderDashboard</span>
              </div>

              <Separator />

              {/* Mobile Main Nav */}
              <nav className="flex flex-col gap-1 p-2">
                {mainNavItems.map(renderMobileNavLink)}
              </nav>

              {/* Mobile Bottom Section */}
              <div className="absolute bottom-0 left-0 right-0">
                <Separator />
                <nav className="flex flex-col gap-1 p-2">
                  {bottomNavItems.map(renderMobileNavLink)}
                  <SheetClose asChild>
                    <button
                      onClick={handleLogoutClick}
                      className="flex items-center gap-3 rounded-lg px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors w-full text-left"
                    >
                      <LogOut className="h-5 w-5 shrink-0" />
                      <span className="text-sm font-medium">Logout</span>
                    </button>
                  </SheetClose>
                </nav>
              </div>
            </SheetContent>
          </Sheet>

          <Link to={basePath} className="flex items-center gap-2">
            <TraderGraphIcon className="h-6 w-6" />
            <span className="text-sm font-semibold">TraderDashboard</span>
          </Link>
        </header>

        {/* Main Content */}
        <main className="flex flex-1 flex-col gap-4 p-4 md:gap-8 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
